import { useState, useEffect, useCallback } from 'react';

// Custom event name for cross-component sync within the same tab
const BUDGET_PREFS_EVENT = 'budget-preferences-changed';

/**
 * Shared hook for budget preference toggles (include monthly payments, include estimated costs).
 * Persists to localStorage and syncs across all component instances via custom events.
 */
export const useBudgetPreferences = () => {
  const [includeMonthlyPayments, setIncludeMonthlyPaymentsState] = useState(() => {
    const saved = localStorage.getItem('includeMonthlyPayments');
    return saved ? JSON.parse(saved) : true;
  });

  const [includeEstimatedCosts, setIncludeEstimatedCostsState] = useState(() => {
    const saved = localStorage.getItem('includeEstimatedCosts');
    return saved ? JSON.parse(saved) : true;
  });

  // Wrapped setters that persist to localStorage and notify other instances
  const setIncludeMonthlyPayments = useCallback(value => {
    setIncludeMonthlyPaymentsState(value);
    localStorage.setItem('includeMonthlyPayments', JSON.stringify(value));
    window.dispatchEvent(
      new CustomEvent(BUDGET_PREFS_EVENT, {
        detail: { key: 'includeMonthlyPayments', value },
      }),
    );
  }, []);

  const setIncludeEstimatedCosts = useCallback(value => {
    setIncludeEstimatedCostsState(value);
    localStorage.setItem('includeEstimatedCosts', JSON.stringify(value));
    window.dispatchEvent(
      new CustomEvent(BUDGET_PREFS_EVENT, {
        detail: { key: 'includeEstimatedCosts', value },
      }),
    );
  }, []);

  // Listen for changes from other component instances in the same tab
  useEffect(() => {
    const handlePrefsChange = e => {
      const { key, value } = e.detail;
      if (key === 'includeMonthlyPayments') {
        setIncludeMonthlyPaymentsState(value);
      } else if (key === 'includeEstimatedCosts') {
        setIncludeEstimatedCostsState(value);
      }
    };

    window.addEventListener(BUDGET_PREFS_EVENT, handlePrefsChange);
    return () => window.removeEventListener(BUDGET_PREFS_EVENT, handlePrefsChange);
  }, []);

  return {
    includeMonthlyPayments,
    setIncludeMonthlyPayments,
    includeEstimatedCosts,
    setIncludeEstimatedCosts,
  };
};
